import firebase from "firebase"

import { db, funEU } from "../../../utils/firebase"

import { buildSchemaFromFields } from "../registry"
import { loadSettings, looksLikeHtml, validateSettings } from "./storage"

/**
 * Run the current Development-tab parser settings against an enquiry-shaped
 * `entry` and (optionally) persist the result back to its doc.
 *
 * Two callers, two modes:
 *   1. Email queue rows / parse modal — `persist: true` (default). Writes the
 *      result + a snapshot of the prompt config to `enquiry_queue/{id}` so
 *      the table picks up the new state via its onSnapshot listener and we
 *      can audit which prompt produced which result.
 *   2. Website enquiries — `persist: false`. Returns the result envelope
 *      without touching Firestore. Used for ad-hoc parsing of website
 *      enquiries where we don't want to write anything back to the source
 *      record yet.
 *
 * Centralising the validation + payload-build + callable invocation keeps
 * both modes in lockstep.
 */
export async function runParse(entry, { persist = true } = {}) {
	const settings = loadSettings()
	validateSettings(settings)

	const schema = buildSchemaFromFields(settings.fields, settings.predictions)
	const callable = funEU.httpsCallable("runEmailAiTool")

	// Prefer comm_id when present so the backend pulls the canonical body +
	// attachments from Storage. Fall back to denormalised inline fields.
	const payload = {
		model: settings.model,
		temperature: settings.temperature,
		system_instructions: settings.system_instructions,
		user_instructions: settings.user_instructions,
		response_format: "json_schema",
		json_schema: schema,
		json_schema_name: "enquiry_ingest",
		json_schema_strict: true,
		custom_variables: {},
		pass_images: false,
	}

	if (entry.comm_id) {
		payload.comm_id = entry.comm_id
	} else {
		const body = entry.body || ""
		payload.inline_email = {
			body,
			body_content_type:
				entry.body_content_type || (looksLikeHtml(body) ? "html" : "text"),
			subject: entry.subject || "",
			from_name: entry.from_name || "",
			from_address: entry.from_address || "",
			occurred_at:
				(entry.received_at?.toDate && entry.received_at.toDate().toISOString()) ||
				entry.received_at_iso ||
				new Date().toISOString(),
		}
	}

	const response = await callable(payload)
	const data = response.data || {}

	if (persist && entry.id) {
		await db
			.collection("enquiry_queue")
			.doc(entry.id)
			.set(
				{
					last_parse_result: data.parsed || null,
					last_parse_output_text: data.output_text || null,
					last_parse_model: data.model_used || settings.model,
					last_parse_usage: data.usage || null,
					last_parse_at: firebase.firestore.FieldValue.serverTimestamp(),
					last_parse_settings: {
						model: settings.model,
						temperature: settings.temperature,
						system_instructions: settings.system_instructions,
						user_instructions: settings.user_instructions,
						fields: settings.fields,
						predictions: settings.predictions,
					},
				},
				{ merge: true }
			)
	}

	return {
		data,
		settings,
	}
}

/**
 * Persist a parse run back onto an `enquiries/{id}` document under the
 * namespaced `ai_parsing` map. Crucially this only touches that one map —
 * every other field on the enquiry (status, agent, client, location,
 * tracking, etc.) is left exactly as-is.
 *
 * Same shape of payload as the email queue saves to `enquiry_queue` docs,
 * just nested rather than flat top-level. Keeping it nested means we can
 * drop the whole map later by writing `FieldValue.delete()` to `ai_parsing`
 * without affecting anything else.
 */
export async function persistWebsiteEnquiryParse(enquiryId, data, settings) {
	if (!enquiryId) return

	await db
		.collection("enquiries")
		.doc(enquiryId)
		.set(
			{
				ai_parsing: {
					result: data.parsed || null,
					output_text: data.output_text || null,
					model: data.model_used || settings.model,
					usage: data.usage || null,
					parsed_at: firebase.firestore.FieldValue.serverTimestamp(),
					settings: {
						model: settings.model,
						temperature: settings.temperature,
						system_instructions: settings.system_instructions,
						user_instructions: settings.user_instructions,
						fields: settings.fields,
						predictions: settings.predictions,
					},
				},
			},
			{ merge: true }
		)
}
